// GeoInfoReader.js
//
// Reads geo-metadata (GSD, geoKeys, UTM zone, pixel↔geo transforms) from
// the user's uploaded GeoTIFF and fires callbacks so the rest of the app
// can use it. Renders nothing.
//
// Data path: reads from the `#file-upload` input element (the actual File
// object the user selected) via geotiff.js `fromBlob`. There is NO network
// fetch — the file is already in memory from the upload. An earlier version
// also tried `fetch('/${imageName}')`, but the CRA dev server doesn't serve
// uploaded files and always returned a 404 HTML page, causing geotiff to
// throw "Invalid byte order value" (it was trying to parse `<!DOCTYPE html>`
// as TIFF bytes). That path has been removed.

import React, { useEffect, useState, useCallback } from 'react';
import { fromBlob } from 'geotiff';

const GeoInfoReader = ({
  tifFile,
  onGsdCalculated,
  onGeoKeysCalculated,
  onPixelToGeoReady,
  onGeoToPixelReady,
  onUtmZoneReady,
}) => {
  const [geoInfo, setGeoInfo] = useState(null);

  // ─── helpers ────────────────────────────────────────────────────────────

  // GSD is only meaningful if the raster's CRS is PROJECTED in metres. For a
  // geographic CRS (EPSG:4326) the bbox is in DEGREES, so this arithmetic would
  // report ~1e7 "cm/px" and every downstream distance -- waypoint spacing,
  // boundary offsets, measurements -- would be silently wrong. UTM codes are
  // 326xx (north) / 327xx (south).
  const isMetreCrs = (epsg) => Boolean(epsg) &&
    ((epsg >= 32601 && epsg <= 32660) || (epsg >= 32701 && epsg <= 32760));

  const calculateGSD = (meta) => {
    const { width, height, bbox } = meta;
    if (!bbox) return null;
    const epsg = meta.geoKeys?.ProjectedCSTypeGeoKey;
    if (!isMetreCrs(epsg)) {
      console.warn(
        `[GeoInfoReader] GeoTIFF CRS ${epsg ?? '(none)'} is not a metre-based UTM ` +
        'projection. The GSD derived from its bounding box is NOT in cm/px and ' +
        'distance-dependent tools will be wrong. Reproject the orthomosaic to UTM.',
      );
      return null;
    }
    const [xMin, yMin, xMax, yMax] = bbox;
    const gsd_width  = (xMax - xMin) / width;
    const gsd_height = (yMax - yMin) / height;
    return ((gsd_width + gsd_height) / 2) * 100; // cm/px, full precision — InfoBar formats the display
  };

  const extractUtmZone = (epsg) => {
    if (epsg >= 32601 && epsg <= 32660) return `${epsg - 32600}N`;
    if (epsg >= 32701 && epsg <= 32760) return `${epsg - 32700}S`;
    return null;
  };

  // ─── main read ──────────────────────────────────────────────────────────

  const processFile = useCallback(async (file) => {
    try {
      const tiff  = await fromBlob(file);
      const image = await tiff.getImage();
      const fd    = image.fileDirectory;

      const meta = {
        width:             image.getWidth(),
        height:            image.getHeight(),
        geoKeys:           image.getGeoKeys(),
        bbox:              image.getBoundingBox(),
        modelTransformation: fd.ModelTransformationTag,
        geoTransformation:   fd.GeoTransformationTag,
      };

      setGeoInfo(meta);

      // GSD
      const gsd = calculateGSD(meta);
      if (gsd !== null && typeof onGsdCalculated === 'function') {
        onGsdCalculated(gsd);
      }

      // GeoKeys (Datum, Zone string, etc.)
      if (typeof onGeoKeysCalculated === 'function') {
        onGeoKeysCalculated(meta.geoKeys);
      }

      // UTM zone
      const epsg = meta.geoKeys?.ProjectedCSTypeGeoKey;
      if (epsg && typeof onUtmZoneReady === 'function') {
        onUtmZoneReady(extractUtmZone(epsg));
      }
    } catch (err) {
      console.error('GeoInfoReader.processFile failed:', err);
    }
  }, [onGsdCalculated, onGeoKeysCalculated, onUtmZoneReady]);

  useEffect(() => {
    if (tifFile) processFile(tifFile);
  }, [tifFile, processFile]);

  // ─── pixel ↔ geo transforms ─────────────────────────────────────────────

  const pixelToGeo = useCallback((x, y) => {
    if (!geoInfo) return null;
    if (geoInfo.modelTransformation) {
      const [a, b, c, d, e, f] = geoInfo.modelTransformation;
      return [a * x + b * y + c, d * x + e * y + f];
    }
    if (geoInfo.geoTransformation) {
      const [a, b, c, d, e, f] = geoInfo.geoTransformation;
      return [a + c * x + d * y, b + e * x + f * y];
    }
    if (geoInfo.bbox) {
      const [xMin, , xMax, yMax] = geoInfo.bbox;
      return [
        xMin + (x / geoInfo.width)  * (xMax - xMin),
        yMax - (y / geoInfo.height) * (yMax - geoInfo.bbox[1]),
      ];
    }
    return null;
  }, [geoInfo]);

  const geoToPixel = useCallback((easting, northing) => {
    if (!geoInfo || isNaN(easting) || isNaN(northing)) return null;
    if (geoInfo.modelTransformation) {
      const [a, b, c, d, e, f] = geoInfo.modelTransformation;
      const det = a * e - b * d;
      if (det !== 0) return [
        Math.round(((easting - c) * e - (northing - f) * b) / det),
        Math.round(((northing - f) * a - (easting - c) * d) / det),
      ];
    }
    if (geoInfo.geoTransformation) {
      const [a, b, c, , e, f] = geoInfo.geoTransformation;
      return [Math.round((easting - a) / c), Math.round((northing - b) / f)];
    }
    if (geoInfo.bbox) {
      const [xMin, yMin, xMax, yMax] = geoInfo.bbox;
      return [
        Math.round((easting  - xMin) / ((xMax - xMin) / geoInfo.width)),
        Math.round((yMax - northing)  / ((yMax - yMin) / geoInfo.height)),
      ];
    }
    return null;
  }, [geoInfo]);

  useEffect(() => {
    if (typeof onPixelToGeoReady === 'function') onPixelToGeoReady(pixelToGeo);
  }, [pixelToGeo, onPixelToGeoReady]);

  useEffect(() => {
    if (typeof onGeoToPixelReady === 'function') onGeoToPixelReady(geoToPixel);
  }, [geoToPixel, onGeoToPixelReady]);

  return null;
};

export default React.memo(GeoInfoReader);
